import { useMutation, useQuery } from '@apollo/client';
import { Form, Modal, Select, notification } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { generateUuid } from '../../shared/utils';
import SelectContact from '../contacts/SelectContact';
import {
  allPartnerTypesQuery,
  createPartnerOnboardingMutation,
  extractAllPartnerTypes,
} from './constants';
import SelectCoordinator from './SelectCoordinator';

function CreatePartnerOnboardingModal({
  visible,
  onCancel,
  onSuccess,
  initialContactId,
}) {
  const [form] = Form.useForm();
  const [partnerType, setPartnerType] = useState(undefined);

  useEffect(() => {
    if (visible && initialContactId) {
      form.setFieldsValue({ contactId: initialContactId });
    }
  }, [visible, initialContactId, form]);
  const [createOnboarding, { loading }] = useMutation(
    createPartnerOnboardingMutation,
  );
  const { data: ptData } = useQuery(allPartnerTypesQuery);
  const partnerTypes = extractAllPartnerTypes(ptData);

  const isFieldWorker = partnerType === 'F';
  const handleFinish = useCallback(
    async (values) => {
      const fieldWorker = values.partnerType === 'F';
      try {
        await createOnboarding({
          variables: {
            partnerOnboarding: {
              _id: generateUuid(),
              contactId: values.contactId,
              partnerType: values.partnerType,
              coordinatorContactId: fieldWorker
                ? values.coordinatorContactId
                : undefined,
              // Region is derived server-side from the contact (FW: from their coordinator).
            },
          },
        });
        notification.success({
          message: 'Created',
          description: 'Partner onboarding record created',
        });
        form.resetFields();
        onSuccess();
      } catch (err) {
        notification.error({ message: 'Error', description: err.message });
      }
    },
    [createOnboarding, form, onSuccess],
  );

  return (
    <Modal
      open={visible}
      onCancel={onCancel}
      title="Add Partner Onboarding"
      onOk={() => form.submit()}
      okText="Create"
      confirmLoading={loading}
      destroyOnClose
      afterClose={() => setPartnerType(undefined)}
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleFinish}
        onValuesChange={(_changed, all) => setPartnerType(all.partnerType)}
      >
        <Form.Item
          name="contactId"
          label="Contact"
          rules={[{ required: true, message: 'Contact is required' }]}
        >
          <SelectContact disabled={!!initialContactId} />
        </Form.Item>
        <Form.Item
          name="partnerType"
          label="Partner Type"
          rules={[{ required: true, message: 'Partner type is required' }]}
        >
          <Select placeholder="Select partner type">
            {partnerTypes.map((pt) => (
              <Select.Option key={pt._id} value={pt._id}>
                {pt.label}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        {isFieldWorker && (
          <Form.Item
            name="coordinatorContactId"
            label="Coordinator"
            rules={[
              {
                required: true,
                message: 'A coordinator is required for field workers',
              },
            ]}
            extra="The field worker inherits this coordinator's region. Regional coordinators and coordinators being onboarded are eligible."
          >
            <SelectCoordinator skip={!isFieldWorker} />
          </Form.Item>
        )}
      </Form>
    </Modal>
  );
}

export default CreatePartnerOnboardingModal;
